import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  get,
  useFormContext,
} from "react-hook-form";
import React, { useMemo, useState } from "react";

import {
  SelectV2,
  type SelectV2Props,
} from "src/components/SelectV2/SelectV2.tsx";

import { composeRefs } from "@exonn/hooks";
import { Flex } from "src/components/Flex.tsx";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { useServerSelectFilter } from "src/system/useServerSelectFilter.ts";
import { useSelect } from "src/system/useSelect.tsx";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";
import type {
  SingleSelectOptions,
  SingleSelectServerOptions,
} from "src/components/Form/Form.types.ts";
import type { GridValidRowModel } from "@mui/x-data-grid-premium";
import { useSelectOptions } from "src/system/useSelectOptions.ts";
import { getDefaultSelectOptions } from "src/system/getDefaultSelectOptions.ts";

export type SingleSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<SelectV2Props, "onChange" | "options"> &
  Omit<ControllerProps<TFieldValues, TName>, "render"> & {
    name: TName;
    forceValue?: any;
    locale?: string;
    displayAsText?: boolean;
    fallbackResources?: GridValidRowModel[];
    labelWidth?: number;
    isButton?: boolean;
    onChange?: (value: any, form: any, resource: any) => void;
    options?: SingleSelectOptions;
    serverOptions?: SingleSelectServerOptions;
  };
// todo: forward ref
export const SingleSelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: SingleSelectFieldProps<TFieldValues, TName>,
) => {
  const form = useFormContext();

  const {
    disabled,
    name,
    label,
    locale,
    options: optionsProp = [],
    onChange: onChangeProp,
    onBlur: onBlurProp,
    serverOptions: serverOptionsProp,
    forceValue,
    displayAsText,
    labelWidth,
    fallbackResources,
    fallbackOptions: fallbackOptionsProp = [],
    ...restComponentProps
  } = props;
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const serverOptions = useMemo(() => {
    if (serverOptionsProp) {
      return {
        ...getDefaultSelectOptions({
          resource: serverOptionsProp?.resource,
        }),
        ...serverOptionsProp,
      };
    }
    return undefined;
  }, [serverOptionsProp]);

  const filter = useServerSelectFilter(
    {
      ...serverOptions,
    },
    false,
  );

  const {
    options: resourceOptions,
    query: {
      isLoading: isLoadingResourceOptions,
      refetch: refetchResourceList,
    },
    getResourceByValue,
  } = useSelect({
    locale,
    resource: serverOptions ? serverOptions.resource : "",
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    include: serverOptions?.include,
    useSimplePagination: true,
    disablePagination: true,
    groupBy: serverOptions?.groupBy,
    groups: serverOptions?.groups,
    processOptions: serverOptions?.processOptions,
    filter: filter,
    sort: serverOptions?.sort,
    queryOptions: {
      enabled: !!serverOptions,
    },
  });

  const customFallbackOptions = useSelectOptions({
    resources: fallbackResources || [],
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    groupBy: serverOptions?.groupBy,
    groups: serverOptions?.groups,
  });

  const fallbackOptions = [
    ...(fallbackOptionsProp || []),
    ...customFallbackOptions,
  ];

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && serverOptions) {
      refetchResourceList();
    }
  };

  const options = useMemo(() => {
    if (serverOptions) return resourceOptions;
    if (Array.isArray(optionsProp)) return optionsProp;

    return getResourceFieldOptions({
      resource: optionsProp.resource,
      field: optionsProp.field,
      values: optionsProp.only,
      t,
    });
  }, [serverOptions, optionsProp, resourceOptions, t]);

  return (
    <Controller
      key={name}
      name={name}
      control={form.control}
      render={({
        field: { value, onChange, onBlur, ref },
        formState: { errors },
      }) => {
        const resolvedValue = forceValue != null ? forceValue : value;

        const isLoadingData = serverOptions ? isLoadingResourceOptions : false;

        if (displayAsText) {
          const option = options.find(
            (option) => option.value === resolvedValue,
          );
          return (
            <Flex>
              <span
                css={labelStyle({
                  width: labelWidth,
                })}
              >
                {label}:
              </span>
              <span css={valueStyle}>{option?.label}</span>
            </Flex>
          );
        }

        return (
          <SelectV2
            error={get(errors?.root || {}, name)?.message}
            onBlur={(event) => {
              onBlurProp?.(event);
              onBlur();
            }}
            fallbackOptions={fallbackOptions}
            label={label}
            options={options}
            onChange={(value) => {
              const resource = getResourceByValue(value);
              onChangeProp?.(value, form, resource);
              onChange(value);
            }}
            isOpen={isOpen}
            isLoadingSelectedOption={isLoadingData}
            disabled={disabled}
            isLoadingData={isLoadingData}
            name={name}
            onOpenChange={handleOpenChange}
            value={resolvedValue}
            {...restComponentProps}
            ref={composeRefs(ref)}
          />
        );
      }}
    />
  );
};

const labelStyle =
  ({ width }: { width?: number }) =>
  (theme: ExactTheme) => css`
    font-size: ${theme.fontSizesV2.body};
    font-weight: ${theme.fontWeights.normal};
    color: ${theme.colorsV2.grey[800]};
    margin-right: 5px;
    display: flex;
    width: ${width != null ? width + "px" : "unset"};
  `;

const valueStyle = (theme: ExactTheme) => css`
  font-size: ${theme.fontSizesV2.body};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colorsV2.grey[900]};
`;
